// Wellcam — Music broadcast (R2 W2).
//
// Permette al TRAINER di trasmettere musica nella stanza LiveKit come traccia
// audio separata (source = ScreenShareAudio). I CLIENTI possono regolare in
// modo INDIPENDENTE volume voce trainer (Microphone) e volume musica.
//
// Architettura:
//
//   Trainer browser:
//     ┌─────────────┐
//     │ <audio> URL │ →  AudioContext  →  GainNode  →  MediaStreamDestination
//     └─────────────┘                                          │
//                                                              ▼
//                              LiveKit.publishTrack(audioTrack, ScreenShareAudio)
//
//   Customer browser:
//     RoomEvent.TrackSubscribed(track, pub, p)
//        if pub.source === Track.Source.ScreenShareAudio and p.role === trainer:
//            attach a <audio data-music-from="<identity>"> separato
//            mostra slider volume "Musica" che agisce su quel <audio>.volume
//
// API esposta come window.WellcamMusic:
//   WellcamMusic.create(room, livekitClient) → istanza con metodi:
//      .loadFromUrl(url)              carica e prepara
//      .loadFromFile(File)            carica da local file
//      .play()                        avvia + pubblica track in room
//      .pause()
//      .stop()                        unpublish track + ferma audio
//      .setLocalVolume(0..1)          volume monitor trainer
//      .setBroadcastVolume(0..1)      gain inviato in room
//      .on(event, cb)                 events: ended, error
//      .getState()                    { playing, duration, currentTime, url }

(function () {
  if (window.WellcamMusic) return;

  class MusicBroadcast {
    constructor(room, livekit) {
      this.room = room;
      this.livekit = livekit; // window.LivekitClient
      this.audioEl = null;
      this.audioCtx = null;
      this.sourceNode = null;
      this.broadcastGain = null;
      this.localGain = null;
      this.destNode = null;
      this.publishedPub = null;
      this.listeners = new Map();
      this.url = null;
    }

    on(event, cb) {
      if (!this.listeners.has(event)) this.listeners.set(event, []);
      this.listeners.get(event).push(cb);
    }
    _emit(event, payload) {
      (this.listeners.get(event) || []).forEach(cb => { try { cb(payload); } catch {} });
    }

    async loadFromUrl(url) {
      this.url = url;
      await this._prepareAudio(url);
    }

    async loadFromFile(file) {
      const objectUrl = URL.createObjectURL(file);
      this.url = file.name;
      await this._prepareAudio(objectUrl);
    }

    async _prepareAudio(src) {
      this._teardown();
      this.audioEl = new Audio();
      this.audioEl.crossOrigin = 'anonymous';
      this.audioEl.src = src;
      this.audioEl.loop = false;
      this.audioEl.preload = 'auto';

      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      // resume on user gesture (Safari)
      if (this.audioCtx.state === 'suspended') {
        try { await this.audioCtx.resume(); } catch {}
      }

      this.sourceNode = this.audioCtx.createMediaElementSource(this.audioEl);
      this.broadcastGain = this.audioCtx.createGain();
      this.broadcastGain.gain.value = 0.7;
      this.localGain = this.audioCtx.createGain();
      this.localGain.gain.value = 0.4; // monitor lower to avoid feedback

      // Split: source → broadcastGain → destNode (LiveKit out)
      //        source → localGain → ctx.destination (trainer ear monitor)
      this.destNode = this.audioCtx.createMediaStreamDestination();
      this.sourceNode.connect(this.broadcastGain);
      this.broadcastGain.connect(this.destNode);
      this.sourceNode.connect(this.localGain);
      this.localGain.connect(this.audioCtx.destination);

      this.audioEl.addEventListener('ended', () => {
        this._emit('ended', { url: this.url });
        this._unpublish();
      });
      this.audioEl.addEventListener('error', (e) => {
        this._emit('error', { url: this.url, message: 'Errore caricamento audio' });
      });
    }

    async play() {
      if (!this.audioEl || !this.audioCtx) throw new Error('Carica prima un brano');
      if (this.audioCtx.state === 'suspended') await this.audioCtx.resume();
      await this.audioEl.play();
      await this._publish();
    }

    pause() {
      if (this.audioEl) this.audioEl.pause();
    }

    stop() {
      if (this.audioEl) { this.audioEl.pause(); this.audioEl.currentTime = 0; }
      this._unpublish();
    }

    async _publish() {
      if (!this.room || !this.destNode) return;
      if (this.publishedPub) return;
      const stream = this.destNode.stream;
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack) return;
      // Source.ScreenShareAudio è il canale "secondario" più idiomatico per
      // audio non-microfono. I client riconoscono questo source per UI dedicata.
      const sourceEnum = this.livekit.Track.Source.ScreenShareAudio;
      this.publishedPub = await this.room.localParticipant.publishTrack(audioTrack, {
        name: 'wellcam-music',
        source: sourceEnum,
        red: false, // disable redundancy per audio (musica è continua)
        dtx: false, // no DTX su musica
      });
    }

    async _unpublish() {
      if (!this.publishedPub) return;
      try {
        await this.room.localParticipant.unpublishTrack(this.publishedPub.track, true);
      } catch (e) { console.warn('[wc-music] unpublish failed', e); }
      this.publishedPub = null;
    }

    setLocalVolume(v) {
      if (this.localGain) this.localGain.gain.value = clamp01(v);
    }
    setBroadcastVolume(v) {
      if (this.broadcastGain) this.broadcastGain.gain.value = clamp01(v);
    }

    getState() {
      return {
        playing: !!(this.audioEl && !this.audioEl.paused),
        duration: this.audioEl ? this.audioEl.duration : 0,
        currentTime: this.audioEl ? this.audioEl.currentTime : 0,
        url: this.url,
        publishing: !!this.publishedPub,
      };
    }

    _teardown() {
      try { this._unpublish(); } catch {}
      if (this.audioEl) { this.audioEl.pause(); this.audioEl.src = ''; this.audioEl = null; }
      if (this.audioCtx) { try { this.audioCtx.close(); } catch {} this.audioCtx = null; }
      this.sourceNode = null;
      this.broadcastGain = null;
      this.localGain = null;
      this.destNode = null;
    }

    destroy() { this._teardown(); }
  }

  function clamp01(v) { return Math.max(0, Math.min(1, Number(v) || 0)); }

  window.WellcamMusic = {
    create: (room, livekit) => new MusicBroadcast(room, livekit),
  };
})();
